import React, { useState, useEffect, useCallback } from 'react';
import api from '../api';

const CONDITION_EMOJI = {
  Clear: '☀️', Clouds: '⛅', Rain: '🌧️', Thunderstorm: '⛈️',
  Drizzle: '🌦️', Snow: '❄️', Mist: '🌫️', Fog: '🌫️', Haze: '🌫️',
};
function condIcon(c) { return CONDITION_EMOJI[c] || '☁️'; }

export default function WeatherWidget() {
  const [weather, setWeather] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [refreshing, setRefreshing] = useState(false);

  const load = useCallback(async (force = false) => {
    try {
      force ? setRefreshing(true) : setLoading(true);
      setError(null);
      const { data } = force
        ? await api.post('/weather/refresh')
        : await api.get('/weather');
      setWeather(data);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to load weather');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  if (loading) return <div className="card"><div className="card-body loading">Loading weather...</div></div>;

  if (error) return (
    <div className="card">
      <div className="card-header" style={{ display: 'flex', justifyContent: 'space-between' }}>
        🌤️ Weather
      </div>
      <div className="card-body">
        <p className="text-gray text-sm">{error}</p>
        {error.includes('Settings') && <a href="/admin/settings" style={{ color: 'var(--teal)', fontSize: 13 }}>→ Go to Settings</a>}
      </div>
    </div>
  );

  const c = weather?.current;
  const days = weather?.daily || [];
  const alerts = days.filter(d => d.alert);

  return (
    <div className="card">
      <div className="card-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <span>🌤️ {weather?.location || 'Weather'}</span>
        <button className="btn btn-ghost btn-sm" onClick={() => load(true)} disabled={refreshing} style={{ padding: '2px 8px', fontSize: 11 }}>
          {refreshing ? '...' : '↻ Refresh'}
        </button>
      </div>
      <div className="card-body" style={{ paddingTop: 8 }}>
        {/* Current */}
        {c && (
          <div style={{ display: 'flex', alignItems: 'center', gap: 16, marginBottom: 12 }}>
            <div style={{ fontSize: 40, lineHeight: 1 }}>{condIcon(c.condition)}</div>
            <div>
              <div style={{ fontSize: 28, fontWeight: 700, color: '#fff', lineHeight: 1 }}>{c.temp}°F</div>
              <div style={{ fontSize: 12, color: '#94a3b8', textTransform: 'capitalize' }}>{c.description} · 💨 {c.wind_mph} mph · 💧 {c.humidity}%</div>
            </div>
          </div>
        )}

        {/* Alerts */}
        {alerts.map((d, i) => (
          <div key={i} className="alert alert-warning" style={{ padding: '6px 10px', fontSize: 12, marginBottom: 6 }}>
            ⚠️ {d.date}: {d.alert}
          </div>
        ))}

        {/* 5-day strip */}
        <div style={{ display: 'grid', gridTemplateColumns: `repeat(${days.length}, 1fr)`, gap: 4 }}>
          {days.map((d, i) => (
            <div key={i} style={{ textAlign: 'center', background: 'rgba(255,255,255,0.04)', borderRadius: 8, padding: '6px 4px' }}>
              <div style={{ fontSize: 11, color: '#64748b', marginBottom: 2 }}>
                {i === 0 ? 'Today' : new Date(d.date + 'T12:00:00').toLocaleDateString('en', { weekday: 'short' })}
              </div>
              <div style={{ fontSize: 18 }}>{condIcon(d.condition)}</div>
              <div style={{ fontSize: 11, color: '#fff', fontWeight: 600 }}>{d.high}°</div>
              <div style={{ fontSize: 10, color: '#64748b' }}>{d.low}°</div>
              {d.rain_mm > 0 && <div style={{ fontSize: 9, color: '#60a5fa' }}>🌧 {d.rain_mm}mm</div>}
            </div>
          ))}
        </div>
        {weather?.stale && <p style={{ fontSize: 10, color: '#475569', marginTop: 6, textAlign: 'right' }}>Cached data · click Refresh</p>}
      </div>
    </div>
  );
}
